// @flow

// If these media window configurations are not specific to hotels, media-album-configuration should be moved to the media-viewer namespace
import type { MediaWindowConfiguration } from '@ta/hotels.media-album-configuration';

import type { MediaFilter } from './restrictedPartnerMedia';

export {
  getMapsClientName,
  default as getClientNameForServlet,
  SERVLET_CLIENT_MAPPING,
} from './servletClients';

export {
  getMediaFilterToRetrieveAllPhotos,
  getMediaFilterToRetrieveDefaultPhotos,
} from './restrictedPartnerMedia';
export type { MediaGroupInput, MediaFilter } from './restrictedPartnerMedia';

export type Source = {|
  height: number,
  width: number,
  url: string,
|};

export type UserProfile = {|
  username: ?string,
  displayName: ?string,
  avatar?: {
    photoSizes: {
      url: string,
    },
  },
  contributionCounts?: {
    sumAllUgc: number,
  },
  hometown?: {
    location: ?{
      additionalNames: {
        long: string,
      },
    },
  },
  route: {
    url: string,
  },
|};

export type VideoSource = {|
  height: number,
  width: number,
  url: string,
  duration: number,
|};

export type MediaProvider = {|
  isLocalProvider?: boolean,
  name?: string,
|};

export type MediaReview = {|
  id: number,
  url: string,
  title: string,
  rating?: number,
  isPhotoOnly: boolean,
  text: string,
|};

export type MediaType = 'p' | 'v' | 'pano' | 'p360' | 'u';
export type SupplierCategory = 'OWNER' | 'STAFF' | 'TRAVELER';

export type Media = {|
  mediaId: number,
  type: MediaType,
  cdnPath?: string, // legacy Oyster panoramas. 360 photos use photoSizes.
  photoSizes: Array<Source>,
  videoSources?: Array<VideoSource>, // videos
  caption?: string,
  supplierCategory?: ?SupplierCategory,
  thumbsUpVotes?: number,
  uploadDate?: string,
  provider?: MediaProvider,
  providerAttributionUrl?: string,
  review?: MediaReview,
  userProfile?: UserProfile,
  loading?: boolean,
  photo360Format?: string,
|};

const mediaAlbumServiceClients = {
  h: 'Hotels',
  hra: 'HR_ATF',
  hrb: 'HR_BTF',
  hr: 'Hotel_Review',
  hf: 'Hotel_Feature',
  hh: 'HotelHighlight',
  sd: 'SmartDeals',
  hl: 'HotelsList',
  hn: 'HotelsNear',
  lm: 'LastMinute',
  sur: 'ShowUserReviews',
  lpdl: 'LocationPhotoDirectLink',
  acs: 'AccommodationCrossSells',
  maps: 'Maps',
  mv: 'MediaViewer',
  nh: 'MobileNearbyHotels',
  r: 'Restaurants',
  vp: 'Vacation_Packages',
  a: 'Attractions',
  ar: 'Attraction_Review',
  un: 'Unknown',
};

export type MediaAlbumServiceClient = $Keys<typeof mediaAlbumServiceClients>;

const mediaAlbumDataStrategies = {
  s: 'Serialized',
  a: 'Async',
  m: 'Media',
  fa: 'FeatureAlbum',
  apd: 'AttractionProductDetail',
  apdhps: 'AttractionProductDetailHeroPhotoSwap',
};

export type MediaAlbumDataStrategy = $Keys<typeof mediaAlbumDataStrategies>;

export type Album = {|
  albumId: number,
  name: string,
  dataStrategy: MediaAlbumDataStrategy,
  totalMediaCount: number,
  offset: number,
  mediaList: Array<Media>,
  mediaCounts?: { [string]: number },
|};

export type WindowPane = {|
  albums: Array<Album>,
|};

export type MediaWindow = {|
  windowPanes: Array<WindowPane>,
|};

export type CompoundMediaId = {
  id: number,
  type: MediaType,
};

export type WindowQueryProps = {|
  root: ?Object,
  location: ?Object,
  windowPanes: ?Array<Array<Album>>,
  loading: boolean,
  passThroughOptions?: Object,
|};

// The album types shown in the atf media window with corresponding album ids
export const AlbumTypes = {
  ALL_ALBUMS: 101,
  ALL_PHOTOS_ALBUM: -125,
  ALL_MEDIA_ALBUM: -127,
  THIN_GEO_PHOTO_ALBUM: -126,
  PANO_ALBUM: 999,
  TRAVELER_ALBUM: 107,
  STORYBOARD_ALBUM: 14,
  ROOM_AND_SUITE_ALBUM: 106,
  DINING_ALBUM: 16,
  HOTEL_AND_AMENITIES: 104,
  POOL_AND_BEACH: 105,
  VIDEO: 14,
  FAVORITE_PHOTOS: 109,
  GYM: 63238,
};

export type AlbumQueryProps = {|
  root: ?Object,
  selectedIndex?: ?number,
  response: ?Object,
  album: ?Object,
  locationId: number,
  client: MediaAlbumServiceClient,
  loading: boolean,
  passThroughOptions: ?Object,
|};

export type FilterOption = {|
  id: any,
  name: string,
  count: number,
  selected: boolean,
|};

export type Filter = {|
  title: string,
  options: Array<FilterOption>,
  onSelection: (id: number) => void,
|};

export type LoadableMedia = {
  loading: boolean,
  error?: boolean,
  media?: Media,
};

export type OffsetMediaArray = {
  media: Array<LoadableMedia>,
  startingIndex: number,
  totalMediaCount: number,
};

export type MediaContentRendererArgs = {
  loading: boolean,
  media: OffsetMediaArray,
  getNextPage: (direction: number) => boolean,
};

export type MediaViewerHashState = {
  locationId: number,
  referenceCompoundMediaId?: ?CompoundMediaId, // The media id we want to load from.
  albumId: number,
  categoryId: number,
  typeId: number,
};

export type MediaViewerState = {
  ...MediaViewerHashState,
  locationName?: ?string,
  locationTotalMediaCount?: ?number,
  referenceGalleryIndex?: ?number,
  media?: ?LoadableMedia,
  referenceMedia?: ?Media,
  selectedIndex?: ?number,
  addedToHistory?: boolean, // Set to true, when we need to back twice to close the MV
};

export const mediaGroups = {
  all: 'ALL_UNRESTRICTED',
  all2: 'ALL_INCLUDING_RESTRICTED',
  traveler: 'TRAVELER',
  mgmt: 'MANAGEMENT',
  staff: 'STAFF',
  un: 'UNKNOWN',
};

export type MediaGroup = $Values<typeof mediaGroups>;

export const mediaGroupIds = {
  ALL_INCLUDING_RESTRICTED: 0,
  ALL_UNRESTRICTED: 1,
  TRAVELER: 2,
  MANAGEMENT: 3,
  STAFF: 4,
  UNKNOWN: -1,
};

export const idToMediaGroup = {
  '0': 'ALL_INCLUDING_RESTRICTED',
  '1': 'ALL_UNRESTRICTED',
  '2': 'TRAVELER',
  '3': 'MANAGEMENT',
  '4': 'STAFF',
};

export const getMediaGroupForString = (sGroup: string): MediaGroup => {
  switch (sGroup) {
    case mediaGroups.all:
    case mediaGroups.all2:
    case mediaGroups.traveler:
    case mediaGroups.staff:
    case mediaGroups.mgmt:
      return sGroup;
    default:
      return mediaGroups.un;
  }
};

export const VIDEO_CATEGORY_ALBUM_ID = 14;

export const getMediaGroupIdForString = (sGroup: string): number =>
  mediaGroupIds[getMediaGroupForString(sGroup)];

export const getMediaGroupStringForId = (typeId: number): MediaGroup =>
  idToMediaGroup[typeId] || mediaGroups.un;

// From enum MediaProvider
export const externalProviders: Map<string, string> = new Map([
  ['BOOKINGCOM', 'Booking.com'],
  ['LEONARDO', 'Leonardo'],
  ['ICE', 'ICE Portal'],
]);

export const explicitlyCreditedProviders: Map<string, string> = new Map([
  ['LEONARDO', 'Leonardo'],
  ['ICE', 'ICE Portal'],
]);

// We want to hide the "Report" button for assets from these providers
export const noReportBtnProviders: Set<string> = new Set(['BOOKINGCOM']);

export const getRHRFiltersWindowConfig = (selectedAlbumId: ?number): MediaWindowConfiguration => {
  switch (selectedAlbumId) {
    case AlbumTypes.ROOM_AND_SUITE_ALBUM:
      return 'rssub';
    case AlbumTypes.HOTEL_AND_AMENITIES:
      return 'hasub';
    case AlbumTypes.POOL_AND_BEACH:
      return 'pbsub';
    case AlbumTypes.VIDEO:
      return 'vidsub';
    case AlbumTypes.FAVORITE_PHOTOS:
      return 'favsub';
    case AlbumTypes.ALL_MEDIA_ALBUM:
    default:
      return 'allsub';
  }
};

export const getFilter = (typeId: number): ?MediaFilter => {
  if (typeId || typeId === 0) {
    const mediaGroup = getMediaGroupStringForId(typeId);
    if (mediaGroup !== mediaGroups.un) {
      // $FlowFixMe flow can't infer that (MediaGroup - 'UNKNOWN') is a subset of MediaGroupInput
      return { mediaGroup };
    }
  }
  return null;
};
